/**
 * Shared dense data-display primitives for demo receipts and charts.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../../design-system/tokens.js";
import { MONO_FONT } from "./styles.js";

export type DemoDataTone = "light" | "dark";

function toneText(tone: DemoDataTone): string {
  return tone === "light" ? GRAY[700] : "#cbd5e1";
}

function toneMuted(tone: DemoDataTone): string {
  return tone === "light" ? GRAY[500] : "#94a3b8";
}

function toneTrack(tone: DemoDataTone): string {
  return tone === "light" ? GRAY[100] : "rgba(15, 23, 42, 0.72)";
}

function joinClassNames(...classes: readonly (string | undefined)[]): string | undefined {
  const joined = classes
    .filter((value): value is string => value !== undefined && value.length > 0)
    .join(" ");
  return joined.length > 0 ? joined : undefined;
}

function clampPercent(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
}

export interface DemoKeyValueItem {
  readonly id: string;
  readonly label: React.ReactNode;
  readonly value: React.ReactNode;
  readonly detail?: React.ReactNode;
  readonly mono?: boolean;
}

export interface DemoKeyValueListProps {
  readonly items: readonly DemoKeyValueItem[];
  readonly tone?: DemoDataTone;
  readonly compact?: boolean;
  readonly labelWidth?: number | string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function DemoKeyValueList({
  items,
  tone = "light",
  compact = false,
  labelWidth = "8rem",
  className,
  style,
}: DemoKeyValueListProps): React.JSX.Element {
  return (
    <dl
      className={className}
      style={{
        display: "grid",
        gap: compact ? 4 : 8,
        gridTemplateColumns: `minmax(5rem, ${typeof labelWidth === "number" ? `${labelWidth}px` : labelWidth}) minmax(0, 1fr)`,
        margin: 0,
        ...style,
      }}
    >
      {items.map((item) => (
        <React.Fragment key={item.id}>
          <dt
            style={{
              color: toneMuted(tone),
              fontSize: compact ? 11 : 12,
              fontWeight: 700,
              lineHeight: 1.45,
            }}
          >
            {item.label}
          </dt>
          <dd
            style={{
              color: toneText(tone),
              fontFamily: item.mono === true ? MONO_FONT : undefined,
              fontSize: compact ? 12 : 13,
              fontVariantNumeric: item.mono === true ? "tabular-nums" : undefined,
              lineHeight: 1.45,
              margin: 0,
              minWidth: 0,
            }}
          >
            {item.value}
            {item.detail !== undefined ? (
              <div style={{ color: toneMuted(tone), fontSize: compact ? 10 : 11, marginTop: 2 }}>
                {item.detail}
              </div>
            ) : null}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export interface DemoMeterSegment {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly color: string;
}

export interface DemoMeterBarProps {
  readonly ariaLabel: string;
  readonly max: number;
  readonly min?: number;
  readonly value?: number;
  readonly segments?: readonly DemoMeterSegment[];
  readonly label?: React.ReactNode;
  readonly height?: number;
  readonly tone?: DemoDataTone;
  readonly className?: string;
  readonly decorative?: boolean;
  readonly style?: React.CSSProperties;
  readonly trackColor?: string;
}

export interface DemoSparkBarItem {
  readonly id: React.Key;
  readonly label: React.ReactNode;
  readonly value: number;
  readonly color?: string;
  readonly title?: string;
  readonly valueLabel?: React.ReactNode;
}

export interface DemoSparkBarChartProps {
  readonly ariaLabel: string;
  readonly bars: readonly DemoSparkBarItem[];
  readonly className?: string;
  readonly gap?: number;
  readonly height?: number;
  readonly max?: number;
  readonly minBarPercent?: number;
  readonly showLabels?: boolean;
  readonly style?: React.CSSProperties;
  readonly tone?: DemoDataTone;
}

export interface DemoIntervalTrackItem {
  readonly id: React.Key;
  readonly label: string;
  readonly startPercent: number;
  readonly widthPercent: number;
  readonly color: string;
  readonly borderColor?: string;
}

export interface DemoIntervalTrackProps {
  readonly ariaLabel: string;
  readonly intervals: readonly DemoIntervalTrackItem[];
  readonly className?: string;
  readonly height?: number;
  readonly style?: React.CSSProperties;
  readonly tone?: DemoDataTone;
  readonly trackColor?: string;
}

export function DemoMeterBar({
  ariaLabel,
  max,
  min = 0,
  value,
  segments,
  label,
  height = 16,
  tone = "light",
  className,
  decorative = false,
  style,
  trackColor,
}: DemoMeterBarProps): React.JSX.Element {
  const explicitValue = value ?? segments?.reduce((sum, segment) => sum + segment.value, 0) ?? min;
  const ariaValue = Math.max(min, Math.min(max, explicitValue));
  let offset = 0;
  return (
    <div
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : ariaLabel}
      aria-valuemax={decorative ? undefined : max}
      aria-valuemin={decorative ? undefined : min}
      aria-valuenow={decorative ? undefined : ariaValue}
      className={joinClassNames("demo-meter-bar", className)}
      role={decorative ? undefined : "meter"}
      style={{
        background: trackColor ?? toneTrack(tone),
        borderRadius: 4,
        height,
        overflow: "hidden",
        position: "relative",
        ...style,
      }}
    >
      {(
        segments ?? [{ id: "value", label: ariaLabel, value: explicitValue, color: PRIMARY[600] }]
      ).map((segment) => {
        const left = clampPercent(offset, min, max);
        const width = clampPercent(offset + segment.value, min, max) - left;
        offset += segment.value;
        return (
          <span
            key={segment.id}
            aria-hidden="true"
            title={`${segment.label}: ${segment.value.toString()}`}
            style={{
              background: segment.color,
              height: "100%",
              left: `${left}%`,
              position: "absolute",
              top: 0,
              transition: "left 150ms ease, width 150ms ease",
              width: `${Math.max(0, width)}%`,
            }}
          />
        );
      })}
      {label !== undefined ? (
        <span
          aria-hidden="true"
          style={{
            color: tone === "light" ? GRAY[700] : "#e2e8f0",
            fontFamily: MONO_FONT,
            fontSize: Math.max(7, Math.floor(height * 0.48)),
            left: 4,
            lineHeight: `${height}px`,
            position: "absolute",
            top: 0,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
}

export function DemoSparkBarChart({
  ariaLabel,
  bars,
  className,
  gap = 4,
  height = 80,
  max,
  minBarPercent = 2,
  showLabels = true,
  style,
  tone = "dark",
}: DemoSparkBarChartProps): React.JSX.Element {
  const resolvedMax = max ?? Math.max(1, ...bars.map((bar) => bar.value));
  const template = `repeat(${Math.max(1, bars.length)}, minmax(0, 1fr))`;
  return (
    <div aria-label={ariaLabel} className={className} role="img" style={style}>
      <div
        style={{
          alignItems: "end",
          display: "grid",
          gap,
          gridTemplateColumns: template,
          height,
        }}
      >
        {bars.map((bar) => (
          <span
            key={bar.id}
            title={bar.title}
            style={{
              alignSelf: "end",
              background: bar.color ?? PRIMARY[500],
              borderRadius: "4px 4px 0 0",
              height: `${Math.max(minBarPercent, (bar.value / resolvedMax) * 100)}%`,
              minHeight: 2,
              width: "100%",
            }}
          />
        ))}
      </div>
      {showLabels ? (
        <div
          aria-hidden="true"
          style={{
            color: toneMuted(tone),
            display: "grid",
            fontSize: 11,
            gap,
            gridTemplateColumns: template,
            marginTop: 4,
            textAlign: "center",
          }}
        >
          {bars.map((bar) => (
            <span key={bar.id}>{bar.label}</span>
          ))}
        </div>
      ) : null}
      {bars.some((bar) => bar.valueLabel !== undefined) ? (
        <div
          aria-hidden="true"
          style={{
            display: "grid",
            gap,
            gridTemplateColumns: template,
            justifyItems: "center",
            marginTop: 4,
          }}
        >
          {bars.map((bar) => (
            <span key={bar.id}>{bar.valueLabel}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function DemoIntervalTrack({
  ariaLabel,
  intervals,
  className,
  height = 12,
  style,
  tone = "dark",
  trackColor,
}: DemoIntervalTrackProps): React.JSX.Element {
  return (
    <div
      aria-label={ariaLabel}
      className={className}
      role="img"
      style={{
        background: trackColor ?? toneTrack(tone),
        borderRadius: 4,
        height,
        overflow: "hidden",
        position: "relative",
        ...style,
      }}
    >
      {intervals.map((interval) => (
        <span
          key={interval.id}
          title={interval.label}
          style={{
            background: interval.color,
            border: `1px solid ${interval.borderColor ?? interval.color}`,
            borderRadius: 4,
            height: "100%",
            left: `${Math.max(0, Math.min(100, interval.startPercent))}%`,
            position: "absolute",
            top: 0,
            width: `${Math.max(0, Math.min(100, interval.widthPercent))}%`,
          }}
        />
      ))}
    </div>
  );
}

export type DemoLegendMarker = "square" | "line";

export interface DemoLegendItem {
  readonly id: string;
  readonly label: React.ReactNode;
  readonly color: string;
  readonly value?: React.ReactNode;
  readonly marker?: DemoLegendMarker;
  readonly dashed?: boolean;
}

export interface DemoLegendProps {
  readonly items: readonly DemoLegendItem[];
  readonly tone?: DemoDataTone;
  readonly compact?: boolean;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function DemoLegend({
  items,
  tone = "light",
  compact = false,
  ariaLabel = "Legend",
  className,
  style,
}: DemoLegendProps): React.JSX.Element {
  return (
    <ul
      aria-label={ariaLabel}
      className={className}
      style={{
        color: toneMuted(tone),
        display: "flex",
        flexWrap: "wrap",
        gap: compact ? "6px 10px" : "8px 14px",
        listStyle: "none",
        margin: 0,
        padding: 0,
        ...style,
      }}
    >
      {items.map((item) => (
        <li
          key={item.id}
          style={{
            alignItems: "center",
            display: "inline-flex",
            gap: compact ? 5 : 7,
            fontSize: compact ? 11 : 12,
            minWidth: 0,
          }}
        >
          {item.marker === "line" ? (
            <svg aria-hidden="true" width={24} height={6} viewBox="0 0 24 6">
              <line
                x1={0}
                y1={3}
                x2={24}
                y2={3}
                stroke={item.color}
                strokeDasharray={item.dashed === true ? "4 3" : undefined}
                strokeWidth={2.5}
              />
            </svg>
          ) : (
            <span
              aria-hidden="true"
              style={{ background: item.color, borderRadius: 3, height: 10, width: 10 }}
            />
          )}
          <span style={{ color: toneText(tone), fontWeight: 600 }}>{item.label}</span>
          {item.value !== undefined ? (
            <span style={{ color: toneMuted(tone), fontFamily: MONO_FONT }}>{item.value}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export const DEMO_METER_COLORS = {
  info: PRIMARY[600],
  ok: SAFE[500],
  warn: "#f59e0b",
  danger: WARN[500],
} as const;
